import React, { useRef } from 'react';

// Keyboard convention shared by every react-select surface in the portal: Enter
// is the key that acts on things. Enter opens a closed menu; Space never does
// (it only types into a searchable input). Arrow keys and clicks keep
// react-select's own behavior.
//
// This goes through react-select's own openMenu() rather than a controlled
// menuIsOpen prop. Driving the prop took its getDerivedStateFromProps path,
// which focuses option 0 and doesn't scroll — so a keyboard user opening a
// long list (the nav timezone picker) landed on the first entry instead of
// their current one, and because tabSelectsValue defaults to true, tabbing
// back out silently changed the value. openMenu focuses the SELECTED option
// and scrolls to it.
//
// Usage: pass the returned `selectRef` and `handleKeyDown` to the Select (or
// CreatableSelect — its ref forwards to the inner Select, so openMenu is
// reachable either way).
export const useEnterOpensMenu = (
  isSearchable: boolean = true,
  onKeyDown?: (e: React.KeyboardEvent) => void,
) => {
  const selectRef = useRef<any>(null);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // The inner Select reads menuIsOpen from props (the state manager owns the
    // value), so props is where the live state is — reading .state gave
    // undefined, i.e. "always closed", which would have made Enter re-open the
    // menu instead of selecting the focused option.
    const menuOpen = !!selectRef.current?.props?.menuIsOpen;
    if (!menuOpen && e.key === 'Enter') {
      e.preventDefault();
      selectRef.current?.openMenu('first');
    } else if (!menuOpen && e.key === ' ' && !isSearchable) {
      // Space is not an activation key here: Enter is. Blocked only when
      // there is no search input for it to type into.
      e.preventDefault();
    }
    onKeyDown?.(e);
  };
  return { selectRef, handleKeyDown };
};
